import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Hourglass, Sparkles, WifiOff, X } from 'lucide-react'
import { arrayUnion, doc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS, daysUntil } from '@shared/model'
import { useAuth } from '@/lib/auth'
import { useBooklets } from '@/hooks/useBooklets'
import { useMyCodes } from '@/hooks/useMyCodes'
import { useOnline } from '@/hooks/useOnline'
import { formatDate, formatDays } from '@/lib/format'
import { cn } from '@/lib/cn'

type NoticeTone = 'warning' | 'brand' | 'accent'

interface Notice {
  id: string
  tone: NoticeTone
  icon: typeof Hourglass
  text: string
  linkTo?: string
  linkLabel?: string
  dismissible: boolean
}

const TONES: Record<NoticeTone, string> = {
  warning: 'bg-warning-soft text-warning border-warning/30',
  brand: 'bg-brand-soft text-brand-ink border-brand-ink/25',
  accent: 'bg-accent text-on-accent border-accent-strong',
}

/**
 * All kommunikation sker i appen – det finns varken push eller nyhetsbrev.
 * Den här raden är därför enda stället där användaren får veta att häftet
 * snart går ut, att nästa period börjat säljas eller att ett kommande häfte
 * håller på att låsas upp.
 */
export function NoticeBar() {
  const { user, profile } = useAuth()
  const { booklets } = useBooklets()
  const { codesByBooklet } = useMyCodes()
  const online = useOnline()

  const dismissed = profile?.dismissedNotices ?? []

  const notices = useMemo<Notice[]>(() => {
    if (!booklets.length) return []
    const result: Notice[] = []

    for (const booklet of booklets) {
      const myCodes = codesByBooklet[booklet.id] ?? []
      const owns = myCodes.length > 0

      if (booklet.status === 'active' && owns) {
        const left = daysUntil(booklet.endDate)
        if (left >= 0 && left <= booklet.reminderDaysBefore) {
          result.push({
            id: `expiring-${booklet.id}-${left}`,
            tone: 'warning',
            icon: Hourglass,
            text:
              left === 0
                ? `Sista dagen att använda ${booklet.periodName}. Passa på!`
                : `${formatDays(left)} kvar av ${booklet.periodName}.`,
            linkTo: '/',
            linkLabel: 'Se häftet',
            dismissible: false,
          })
        }
      }

      if (booklet.status === 'upcoming' && owns) {
        result.push({
          id: `upcoming-${booklet.id}`,
          tone: 'brand',
          icon: CalendarClock,
          text: `Ditt nästa häfte aktiveras ${formatDate(booklet.startDate)}.`,
          dismissible: true,
        })
      }

      if (booklet.status === 'active' && !owns) {
        result.push({
          id: `available-${booklet.id}`,
          tone: 'accent',
          icon: Sparkles,
          text: `${booklet.periodName} finns att köpa – fråga en spelare i föreningen.`,
          linkTo: '/mina-koder',
          linkLabel: 'Aktivera kod',
          dismissible: true,
        })
      }
    }

    return result.filter((notice) => !dismissed.includes(notice.id))
  }, [booklets, codesByBooklet, dismissed])

  // Offline är ett tillstånd som ska synas hela tiden, inte en varning som
  // dyker upp först när man försöker lösa in. Står man i en butik med dålig
  // täckning vill man veta det innan man börjar hålla in knappen.
  if (!online) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 pt-3">
        <div className="flex items-center gap-3 rounded-card border border-warning/30 bg-warning-soft px-3.5 py-2.5 text-sm font-semibold text-warning">
          <WifiOff size={18} className="shrink-0" aria-hidden />
          <p className="flex-1 text-balance">
            Ingen uppkoppling. Du kan läsa häftet, men rabatter går inte att använda förrän du är
            online igen.
          </p>
        </div>
      </div>
    )
  }

  if (!notices.length) return null

  async function dismiss(id: string) {
    if (!user) return
    try {
      await updateDoc(doc(db, COLLECTIONS.users, user.uid), {
        dismissedNotices: arrayUnion(id),
      })
    } catch {
      // Offline – notisen kommer tillbaka nästa gång, vilket är helt okej.
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-2 px-4 pt-3">
      {notices.map((notice) => (
        <div
          key={notice.id}
          className={cn(
            'flex items-center gap-3 rounded-card border px-3.5 py-2.5 text-sm font-semibold',
            TONES[notice.tone],
          )}
        >
          <notice.icon size={18} className="shrink-0" aria-hidden />
          <p className="flex-1 text-balance">{notice.text}</p>
          {notice.linkTo && (
            <Link to={notice.linkTo} className="shrink-0 underline underline-offset-2">
              {notice.linkLabel}
            </Link>
          )}
          {notice.dismissible && (
            <button
              type="button"
              onClick={() => void dismiss(notice.id)}
              aria-label="Dölj meddelandet"
              className="tap-safe -mr-1 shrink-0 rounded-pill p-1 opacity-70 hover:opacity-100"
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
